import type { MeResponse, OfflineSnapshot, VisitDto } from '@kominiarz/shared';
import { setOfflineCopyAt } from './appStatus';
import { idbDelete, idbGet, idbSet } from './idb';

/**
 * Read-only copy of upcoming work on this device. Refreshed while online; when a GET
 * request cannot reach the server, the API client answers it from this copy.
 */
/** Bump when the stored data changes shape (v2: team visits, several services); older copies are dropped. */
const KEY = 'offline-copy-v2';
const OLD_KEYS = ['offline-copy'];
/** Plain GET responses kept as they are (the app needs them to render at all). */
const KEPT_RESPONSES = ['/auth/me', '/users', '/service-types', '/settings'] as const;

interface OfflineCopy {
  userId: number;
  snapshot: OfflineSnapshot;
  responses: Record<string, unknown>;
}

let copy: OfflineCopy | null = null;

/** Loads the stored copy into memory; call before the first render. */
export async function loadOfflineCopy(): Promise<void> {
  copy = (await idbGet<OfflineCopy>(KEY)) ?? null;
  setOfflineCopyAt(copy?.snapshot.generatedAt ?? null);
  for (const key of OLD_KEYS) void idbDelete(key);
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

let syncing: Promise<void> | null = null;

/** Downloads a fresh copy. Failures are silent: the previous copy stays. */
export function syncOfflineCopy(userId: number): Promise<void> {
  syncing ??= (async () => {
    try {
      const [snapshot, ...kept] = await Promise.all([
        fetchJson<OfflineSnapshot>('/offline/snapshot'),
        ...KEPT_RESPONSES.map((p) => fetchJson<unknown>(p)),
      ]);
      const me = kept[0] as MeResponse;
      if (me.user?.id !== userId) return;
      copy = { userId, snapshot, responses: Object.fromEntries(KEPT_RESPONSES.map((p, i) => [p, kept[i]])) };
      await idbSet(KEY, copy);
      setOfflineCopyAt(snapshot.generatedAt);
    } catch {
      // Offline or signed out meanwhile; keep what we have.
    } finally {
      syncing = null;
    }
  })();
  return syncing;
}

/** Sign-out or another person signing in: nothing of the previous user stays behind. */
export async function clearOfflineCopy(): Promise<void> {
  copy = null;
  setOfflineCopyAt(null);
  await idbDelete(KEY);
  try {
    await caches.delete('photos-v1');
  } catch {
    // Cache API unavailable.
  }
}

export function offlineCopyUserId(): number | null {
  return copy?.userId ?? null;
}

function overlaps(v: VisitDto, from: string | null, to: string | null): boolean {
  return (!to || v.startAt < new Date(to).toISOString()) && (!from || v.endAt > new Date(from).toISOString());
}

/** The answer to a GET request from the offline copy, or undefined if it has none. */
export function offlineAnswer(path: string): unknown {
  if (!copy) return undefined;
  const url = new URL(path, 'http://offline');
  const { snapshot, responses } = copy;

  if (!url.search && url.pathname in responses) return responses[url.pathname];

  if (url.pathname === '/visits') {
    const from = url.searchParams.get('from');
    const to = url.searchParams.get('to');
    const filter = url.searchParams.get('filter') ?? 'all';
    const visits = snapshot.visits.filter((v) => {
      if (!overlaps(v, from, to)) return false;
      if (filter === 'mine') return v.assignees.some((u) => u.id === copy!.userId);
      if (filter === 'team') return v.assignees.length > 1;
      if (filter.startsWith('user:')) return v.assignees.some((u) => u.id === Number(filter.slice(5)));
      return true;
    });
    return { visits };
  }

  const visit = /^\/visits\/(\d+)$/.exec(url.pathname);
  if (visit) return snapshot.details.find((d) => d.visit.id === Number(visit[1]));

  const client = /^\/clients\/(\d+)$/.exec(url.pathname);
  if (client) return snapshot.clients.find((c) => c.client.id === Number(client[1]));

  return undefined;
}
